/**
 * Presentación de datos de cría (FRONTEND_INTEGRATION.md §7). Funciones puras.
 *
 * Reglas:
 * - El frontend no calcula: no recalcula porcentajes ni promedios ni ordena.
 * - `valor: null` nunca se muestra como 0, "-" sin explicación ni NaN.
 * - Fechas productivas `YYYY-MM-DD` se muestran tal cual (sin `new Date(...)`, que las
 *   interpreta en UTC y puede correrlas un día).
 */
import {
  MOTIVOS,
  MOTIVOS_EXCLUSION,
  INCIDENCIAS_CALIDAD,
} from "./criaCatalogo";

export const ESTADOS_DATO = Object.freeze({
  completo: { etiqueta: "", clase: "" },
  provisional: { etiqueta: "Provisional", clase: "cria-tag--provisional" },
  estimado: { etiqueta: "Estimado", clase: "cria-tag--estimado" },
  insuficiente: { etiqueta: "Sin datos", clase: "cria-tag--insuficiente" },
  inconsistente: {
    etiqueta: "Inconsistente",
    clase: "cria-tag--inconsistente",
  },
  no_aplica: { etiqueta: "No aplica", clase: "cria-tag--no-aplica" },
});

const DECIMALES_POR_UNIDAD = {
  pct: 1,
  por_100_vientres: 1,
  dias: 1,
  kg: 2,
  "kg/ha": 2,
  "kg/campania": 2,
  ha: 4,
  proporcion: 1,
};

const SUFIJO_POR_UNIDAD = {
  pct: "%",
  por_100_vientres: " por 100 vientres",
  dias: " días",
  kg: " kg",
  "kg/ha": " kg/ha",
  "kg/campania": " kg/campaña",
  ha: " ha",
  proporcion: "%",
};

/**
 * Unidad del numerador de un cociente: el subtotal parcial no está en la unidad del
 * indicador (kg/ha → kg totales; % y por 100 vientres → conteos sin unidad).
 */
const UNIDAD_NUMERADOR = {
  "kg/ha": "kg",
  "kg/campania": "kg",
  kg: "kg",
  pct: null,
  por_100_vientres: null,
  proporcion: null,
  dias: "dias",
};

/** Hoy en la fecha local del navegador, `YYYY-MM-DD` (no UTC). */
export function fechaHoyLocal(ahora = new Date()) {
  const y = ahora.getFullYear();
  const m = String(ahora.getMonth() + 1).padStart(2, "0");
  const d = String(ahora.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

/** `YYYY-MM-DD` → `DD/MM/YYYY` sin pasar por Date. Devuelve "" si no es una fecha válida. */
export function formatearFecha(fecha) {
  if (typeof fecha !== "string") return "";
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(fecha);
  if (!m) return "";
  return `${m[3]}/${m[2]}/${m[1]}`;
}

/** Zona del establecimiento para mostrar instantes de auditoría. */
export const ZONA_ESTABLECIMIENTO = "America/Argentina/Buenos_Aires";

/**
 * Instante de auditoría (`created_at`, `updated_at`, `cerrada_at`: UTC con zona) → `DD/MM/YYYY`
 * en la fecha local del establecimiento (§7.5). No usar `formatearFecha`, que recorta el texto
 * y de noche muestra el día siguiente.
 */
export function formatearInstante(instante, { conHora = false } = {}) {
  if (typeof instante !== "string" || !instante) return "";
  const fecha = new Date(instante);
  if (Number.isNaN(fecha.getTime())) return "";
  return fecha.toLocaleString("es-AR", {
    timeZone: ZONA_ESTABLECIMIENTO,
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    ...(conHora ? { hour: "2-digit", minute: "2-digit", hour12: false } : {}),
  });
}

export function esFechaIso(valor) {
  return typeof valor === "string" && /^\d{4}-\d{2}-\d{2}$/.test(valor);
}

function numeroLocal(n, decimales) {
  return n.toLocaleString("es-AR", {
    minimumFractionDigits: 0,
    maximumFractionDigits: decimales,
  });
}

/**
 * Formatea un número según su unidad. `proporcion` (0..1) se convierte a % solo al mostrar.
 * Devuelve "" si el valor no es un número finito.
 */
export function formatearValor(
  valor,
  unidad,
  { conUnidad = true, decimales } = {}
) {
  if (typeof valor !== "number" || !Number.isFinite(valor)) return "";
  const v = unidad === "proporcion" ? valor * 100 : valor;
  const dec = decimales ?? DECIMALES_POR_UNIDAD[unidad] ?? 2;
  const texto = numeroLocal(v, dec);
  return conUnidad ? `${texto}${SUFIJO_POR_UNIDAD[unidad] || ""}` : texto;
}

export function textoMotivo(codigo) {
  return (
    MOTIVOS[codigo]?.texto ||
    `Motivo: ${String(codigo || "").replace(/_/g, " ")}.`
  );
}

export function textoMotivoExclusion(codigo) {
  return (
    MOTIVOS_EXCLUSION[codigo] ||
    `Fuera de clasificación: ${String(codigo || "").replace(/_/g, " ")}.`
  );
}

export function incidenciaCalidad(codigo) {
  return (
    INCIDENCIAS_CALIDAD[codigo] || {
      severidad: "info",
      significado: String(codigo || "").replace(/_/g, " "),
      correccion: "Consultar con soporte.",
    }
  );
}

export function etiquetaEstadoDato(estado) {
  return ESTADOS_DATO[estado]?.etiqueta ?? "";
}

/**
 * Presentación de un indicador `{valor, unidad, numerador, denominador, estado_dato, motivos,
 * cobertura, n}`. No inventa números: con `valor: null` devuelve el rótulo del estado.
 *
 * @returns {{ texto: string, etiqueta: string, estado: string, atenuado: boolean,
 *   parcial: string, motivos: string[], tooltip: string, esNulo: boolean }}
 */
export function presentarIndicador(ind) {
  const estado = ind?.estado_dato || (ind ? "completo" : "insuficiente");
  const motivos = (ind?.motivos || []).map(textoMotivo);
  const valor = ind?.valor;
  const esNulo = valor === null || valor === undefined;
  let texto;
  let parcial = "";
  if (esNulo) {
    if (estado === "no_aplica") texto = "No aplica";
    else if (estado === "inconsistente") texto = "Inconsistente";
    else texto = "Sin datos";
    if (estado === "insuficiente" && typeof ind?.numerador === "number") {
      parcial = `Parcial: ${formatearValor(
        ind.numerador,
        UNIDAD_NUMERADOR[ind.unidad] ?? null
      )}`;
    }
  } else {
    texto = formatearValor(valor, ind.unidad);
  }
  const etiqueta =
    estado === "insuficiente" && !esNulo
      ? "Parcial"
      : esNulo
      ? ""
      : etiquetaEstadoDato(estado);
  const partes = [];
  if (
    typeof ind?.numerador === "number" &&
    typeof ind?.denominador === "number"
  ) {
    partes.push(
      `${formatearValor(ind.numerador, null, {
        conUnidad: false,
      })} / ${formatearValor(ind.denominador, null, { conUnidad: false })}`
    );
  }
  if (typeof ind?.n === "number") partes.push(`n = ${ind.n}`);
  const cobertura = textoCobertura(ind?.cobertura);
  if (cobertura) partes.push(`Cobertura ${cobertura}`);
  partes.push(...motivos);
  return {
    texto,
    etiqueta,
    estado,
    atenuado: estado === "inconsistente" && !esNulo,
    parcial,
    motivos,
    tooltip: partes.join(" · "),
    esNulo,
  };
}

/**
 * Cobertura de un indicador. Llega como porcentaje (preñez: `cobertura` 0..100) o como objeto:
 * `{conocidos, total, proporcion}` (producción) o `{usados, total, excluidos}` (ficha).
 */
export function textoCobertura(cobertura) {
  if (typeof cobertura === "number") return formatearValor(cobertura, "pct");
  if (!cobertura || typeof cobertura !== "object") return "";
  const parte = cobertura.conocidos ?? cobertura.usados;
  if (typeof parte !== "number" || typeof cobertura.total !== "number")
    return "";
  return `${parte} de ${cobertura.total}`;
}

/** Concepción estimada: "≈ 10/11/2021 (± 11 días, estimada por el parto)". */
const ORIGEN_CONCEPCION = {
  observada: "observada",
  ecografia: "por ecografía",
  tacto: "por tacto",
  estimada_por_parto: "estimada por el parto",
};

export function textoConcepcion(concepcion) {
  if (!concepcion?.fecha) return "Sin datos";
  const fecha = formatearFecha(concepcion.fecha);
  const origen =
    ORIGEN_CONCEPCION[concepcion.origen] || concepcion.origen || "";
  if (concepcion.origen === "observada" && !concepcion.margen_dias) {
    return `${fecha} (observada)`;
  }
  const margen =
    typeof concepcion.margen_dias === "number"
      ? `± ${concepcion.margen_dias} días`
      : "";
  const detalle = [margen, origen].filter(Boolean).join(", ");
  return `≈ ${fecha}${detalle ? ` (${detalle})` : ""}`;
}

/** Posición de ranking: "3 (empate)" si comparte puesto con la anterior; "—" si no clasifica. */
export function textoPosicion(fila) {
  if (fila?.posicion === null || fila?.posicion === undefined)
    return "Fuera de clasificación";
  return fila?.explicacion?.empata_con_anterior
    ? `${fila.posicion} (empate)`
    : String(fila.posicion);
}

export const ESTADOS_CAMPANIA = Object.freeze({
  abierta: { etiqueta: "Abierta", clase: "cria-tag--provisional" },
  cerrada: { etiqueta: "Cerrada", clase: "cria-tag--completo" },
  rectificada: { etiqueta: "Rectificada", clase: "cria-tag--estimado" },
});

/** "Rectificada · rev. 7" para rectificadas; la etiqueta simple para el resto. */
export function etiquetaCampania(campania) {
  const base =
    ESTADOS_CAMPANIA[campania?.estado]?.etiqueta || campania?.estado || "";
  if (campania?.estado === "rectificada" && campania?.revision != null) {
    return `${base} · rev. ${campania.revision}`;
  }
  return base;
}
